<template>
  <div class="a-select" :class="{compact: compact, focused: isFocused, empty: !isNotEmpty}">
    <button v-if="compact" @click.stop="isDropdownOpen=true">
      <slot></slot>
    </button>

    <template v-if="!compact">
      <input
        type="search"
        ref="search"
        :value="search"
        :disabled="isDisabled"
        @input="changeAvailableOptions"
        @focus="onFocus"
        @blur="onBlur"
        @click="onFocus"
        @keydown.up="typeAheadUp"
        @keydown.down="typeAheadDown"
        @keyup.enter="onEnter"
        v-focus="isAutofocus"
      >

      <label v-if="label">{{ appliedLabel }}</label>

      <a-select-tag
        v-if="multiple"
        v-for="option in selected"
        :key="option[optionKey]"
        :tagText="option[optionLabel]"
        @deselect="deselect(option)"
      />
    </template>

    <div v-show="isDropdownOpen" v-on-clickaway="hideDropdown" class="dropdown-wrap">
      <input
        v-if="compact"
        type="search"
        ref="searchcompact"
        :value="search"
        :disabled="isDisabled"
        @input="changeAvailableOptions"
        @focus="onFocus"
        @blur="onBlur"
        @click="onFocus"
        @keydown.up="typeAheadUp"
        @keydown.down="typeAheadDown"
        @keyup.enter="onEnter"
        v-focus="true"
      >
      <a-select-dropdown
        @hide="isDropdownOpen=false"
        @select="select"
        :options="getOptions()"
        :selectedOptions="selected"
        :optionLabel="optionLabel"
        :optionKey="optionKey"
        :typeAheadPointer="typeAheadPointer"
        :align="align"
        :multiple="multiple"
        :compact="compact"
        :cohort="userCohort"
      />
    </div>

    <span class="error" v-if="error">{{ error }}</span>
    <span v-if="required && !isNotEmpty" class="error">{{ requiredMessage }}</span>
  </div>
</template>

<script>
import aSelectDropdown from './a-select/a-select-dropdown.vue'
import aSelectTag from './a-select/a-select-tag.vue'
import TypeAheadPointer from '../mixins/typeahead.js'
import { mixin as Clickaway } from 'vue-clickaway'

export default {
  name: 'a-select',
  components: {
    aSelectDropdown,
    aSelectTag,
  },
  mixins: [TypeAheadPointer, Clickaway],
  props: {
    value: {
      default: '',
    },
    options: {
      type: Array,
      required: true,
    },
    optionKey: {
      type: String,
      default: 'value',
    },
    optionLabel: {
      type: String,
      default: 'text',
    },
    compact: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    align: {
      type: String,
      default: 'left',
    },
    label: {
      type: String,
      default: '',
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    isAutofocus: {
      type: Boolean,
      default: true,
    },
    error: {
      type: String,
      default: '',
    },
    name: {
      type: String,
      default: '',
    },
    required: {
      type: Boolean,
      default: false,
    },
    requiredLabel: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selected: this.value || [],
      isDropdownOpen: false,
      isFocused: false,
      searchQuery: null,
    }
  },
  computed: {
    userCohort() {
      return this.$store.getters.userCohort
    },
    search() {
      if (!this.multiple) {
        if (this.searchQuery !== null) {
          return this.searchQuery
        }
        const foundOption = this.getOptions().find(option => {
          return option[this.optionKey] === this.selected[this.optionKey]
        })
        if (foundOption) {
          return foundOption[this.optionLabel] || ''
        }
      } else if (this.searchQuery !== null) {
        return this.searchQuery
      }
      return ''
    },
    filtered() {
      let filteredOptions
      if (this.multiple) {
        filteredOptions = this.getOptions().filter(option =>
          option[this.optionLabel].toLowerCase().includes(this.search.toLowerCase())
        )
      } else {
        filteredOptions = this.getOptions()
      }
      return _.sortBy(filteredOptions, [
        option => {
          const levels = { registered: 1, basic: 2, advanced: 3 }
          return levels[option.level] || 0
        },
      ])
    },
    isNotEmpty() {
      return this.checkSelected(this.selected)
    },
    requiredMessage() {
      return `${this.label} is required`
    },
    appliedLabel() {
      let label = this.label
      if (this.requiredLabel) {
        label += ' *'
      }
      return label
    },
  },
  methods: {
    getOptions() {
      const initOptions = this.options || []
      if (this.searchQuery) {
        const filteredOptions = initOptions.filter(option => {
          let queries = this.searchQuery
          if (queries) {
            queries = queries.toLowerCase().split(' ')
          }
          let fullLabel = option[this.optionLabel] || ''
          if (fullLabel) {
            fullLabel = fullLabel.toLowerCase()
          }
          if (option.menuPath) {
            fullLabel = fullLabel + ' ' + option.menuPath.toLowerCase()
          }
          return queries.every(query => fullLabel.includes(query))
        })
        const flattenOptions = filteredOptions.map(option => {
          const flattenOption = _.clone(option)
          flattenOption.menuPath = '/'
          if (option.menuPath && option.menuPath !== '/') {
            flattenOption.labelPrefix = option.menuPath.substring(1)
          }
          return flattenOption
        })
        return flattenOptions
      }
      return initOptions
    },
    select(payload) {
      const { option, isAboveLevel } = payload
      if (option.level === 'registered') {
        return this.$emit('launchregister')
      }
      if ((option.level === 'basic' || option.level === 'advanced') && !isAboveLevel) {
        return this.$emit('launchsubscribe')
      }
      if (this.multiple) {
        if (this.isSelected(option)) {
          this.deselect(option)
        } else {
          this.selected.push(option)
        }
        this.searchQuery = null
      } else {
        this.searchQuery = null
        this.selected = option
        this.isDropdownOpen = false
      }
    },
    deselect(option) {
      this.selected.splice(this.selected.findIndex(select => select[this.optionKey] === option[this.optionKey]), 1)
    },
    isSelected(option) {
      if (this.multiple) {
        return this.selected.find(select => select[this.optionKey] === option[this.optionKey])
      } else if (this.selected) {
        return this.selected[this.optionKey] === option[this.optionKey]
      } else {
        return false
      }
    },
    focus() {
      if (this.multiple) {
        this.$refs.search.focus()
      }
    },
    onFocus() {
      if (!this.isDisabled) {
        this.isDropdownOpen = true
        this.isFocused = true
      }
    },
    onBlur() {
      this.isDropdownOpen = false
      this.isFocused = false
      this.searchQuery = null
    },
    onEnter() {
      this.typeAheadSelect(this.userCohort)
    },
    checkSelected(obj) {
      for (var prop in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, prop)) {
          if (obj[prop]) {
            return true
          }
        }
      }
      return false
    },
    changeAvailableOptions(e) {
      this.searchQuery = e.target.value
    },
    hideDropdown() {
      if (this.compact) {
        this.isDropdownOpen = false
      }
    },
  },
  watch: {
    value(val) {
      this.selected = val || []
    },
    selected(val) {
      if (this.name) {
        this.$emit('input', { name: this.name, value: val[this.optionKey] })
      } else {
        this.$emit('input', val)
      }
    },
    isDropdownOpen(value) {
      if (value && this.compact) {
        Vue.nextTick(() => {
          this.$refs.searchcompact.focus()
        })
      }
    },
  },
}
</script>

<style scoped>
.a-select {
  display: inline-block;
  position: relative;
}
.a-select::after {
  background-color: #95a1aa;
  content: '';
  position: absolute;
  top: 41px;
  left: 0;
  width: 100%;
  height: 1px;
  transform-origin: center bottom;
  transition: background-color 180ms cubic-bezier(0.4, 0, 0.2, 1), transform 180ms cubic-bezier(0.4, 0, 0.2, 1);
}
.a-select.compact::after {
  height: 0;
}
.a-select.focused::after {
  background-color: #0381ff;
  transform: translateY(100%) scaleY(2);
}
.a-select input {
  font-size: 13px;
  line-height: 1;
  padding: 16px 0 8px;
  width: 100%;
  background-color: transparent;
}
.a-select input:focus {
  outline: none;
}

.a-select label {
  font-weight: 400;
  color: #95a1aa;
  cursor: text;
  font-size: 13px;
  line-height: 1;
  pointer-events: none;
  position: absolute;
  top: 19px;
  left: 0;
  transform-origin: left top;
  transition: color 180ms cubic-bezier(0.4, 0, 0.2, 1), transform 180ms cubic-bezier(0.4, 0, 0.2, 1);
}
.focused > label {
  color: #0381ff;
  transform: translateY(-146%) scale(0.92, 0.92);
}
.a-select:not(.empty):not(.focused) > label {
  color: #95a1aa;
  transform: translateY(-146%) scale(0.92, 0.92);
}
.a-select button {
  border: none;
  box-shadow: none;
}
.compact button {
  box-shadow: none;
  padding: 0;
  min-width: 0;
  background: #fff;
}
.compact {
  border: none;
}
.dropdown-wrap {
  background-color: #fff;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16), 0 3px 6px rgba(0, 0, 0, 0.23);
  position: absolute;
  top: calc(100% + 1px);
  z-index: 10;
}
.compact .dropdown-wrap {
  right: 0;
  min-width: 350px;
}
.dropdown-wrap input {
  padding: 5px 10px 10px;
}
.a-select input[type='search'] {
  border: none;
  margin: 0;
}
.error {
  color: red;
  font-size: 12px;
  background: transparent !important;
}
</style>
